import supabase from './supabaseClient';

export interface BareAct {
  id: string;
  title: string;
  title_hi?: string;
  slug: string;
  category: string;
  subcategory?: string;
  year: number;
  description: string;
  description_hi?: string;
  content?: string;
  content_hi?: string;
  pdf_url?: string;
  txt_url?: string;
  image_url?: string;
  is_featured: boolean;
  last_updated: string;
  created_at: string;
}

export interface BareActSection {
  id: string;
  act_id: string;
  section_number: string;
  title: string;
  title_hi?: string;
  content: string;
  content_hi?: string;
  order_index: number;
}

export interface BareActWithSections extends BareAct {
  sections?: BareActSection[];
  related_acts?: BareAct[];
}

// Cache keys for improved performance
const CACHE_KEYS = {
  BARE_ACTS: 'bare_acts',
  BARE_ACTS_BY_CATEGORY: (category: string) => `bare_acts_category_${category}`,
  FEATURED_BARE_ACTS: (limit: number) => `featured_bare_acts_${limit}`,
  BARE_ACT_DETAIL: (slug: string) => `bare_act_detail_${slug}`,
  SEARCH_RESULTS: (query: string) => `search_bare_acts_${query}`,
  TTL: {
    SHORT: 5 * 60 * 1000,  // 5 minutes
    MEDIUM: 30 * 60 * 1000, // 30 minutes
    LONG: 24 * 60 * 60 * 1000 // 24 hours
  }
};

// Cache helpers with compression for larger objects
const setToCache = <T>(key: string, data: T, expirationMs: number = CACHE_KEYS.TTL.MEDIUM): void => {
  const item = {
    value: data,
    expiry: Date.now() + expirationMs,
  };
  try {
    // Check if data is large enough to benefit from compression
    const stringData = JSON.stringify(item);
    if (stringData.length > 10000) {
      // Only use compression for large objects
      localStorage.setItem(`${key}_size`, stringData.length.toString());
      
      // Split large strings to avoid storage limits
      const chunkSize = 512000; // ~500KB chunks
      const chunks = Math.ceil(stringData.length / chunkSize);
      
      for (let i = 0; i < chunks; i++) {
        const chunk = stringData.substring(i * chunkSize, (i + 1) * chunkSize);
        localStorage.setItem(`${key}_chunk_${i}`, chunk);
      }
      
      localStorage.setItem(`${key}_chunks`, chunks.toString());
    } else {
      // Small data - store normally
      localStorage.setItem(key, stringData);
    }
  } catch (e) {
    console.error('Error storing in cache:', e);
    // Clean up any partial chunks if storage failed
    try {
      const chunks = parseInt(localStorage.getItem(`${key}_chunks`) || '0');
      for (let i = 0; i < chunks; i++) {
        localStorage.removeItem(`${key}_chunk_${i}`);
      }
      localStorage.removeItem(`${key}_chunks`);
      localStorage.removeItem(`${key}_size`);
    } catch (cleanupError) {
      console.error('Error cleaning up cache:', cleanupError);
    }
  }
};

const getFromCache = <T>(key: string): T | null => {
  try {
    // Check if this is a chunked item
    const chunks = parseInt(localStorage.getItem(`${key}_chunks`) || '0');
    
    let itemStr = '';
    if (chunks > 0) {
      // Reconstruct from chunks
      for (let i = 0; i < chunks; i++) {
        const chunk = localStorage.getItem(`${key}_chunk_${i}`);
        if (!chunk) return null; // Missing chunk
        itemStr += chunk;
      }
    } else {
      // Normal item
      itemStr = localStorage.getItem(key) || '';
    }
    
    if (!itemStr) return null;

    const item = JSON.parse(itemStr);
    if (Date.now() > item.expiry) {
      // Clean up expired items
      if (chunks > 0) {
        for (let i = 0; i < chunks; i++) {
          localStorage.removeItem(`${key}_chunk_${i}`);
        }
        localStorage.removeItem(`${key}_chunks`);
        localStorage.removeItem(`${key}_size`);
      } else {
        localStorage.removeItem(key);
      }
      return null;
    }
    
    return item.value as T;
  } catch (e) {
    console.error('Error retrieving from cache:', e);
    return null;
  }
};

// Connection speed detection
const isLowBandwidth = (): boolean => {
  if ('connection' in navigator && (navigator as any).connection) {
    const conn = (navigator as any).connection;
    return conn.effectiveType === 'slow-2g' ||
           conn.effectiveType === '2g' ||
           conn.saveData === true;
  }
  return false;
};

/**
 * Fetch all bare acts with progressive loading
 */
export const fetchAllBareActs = async (): Promise<BareAct[]> => {
  try {
    // Always check cache first for instant loading
    const cachedData = getFromCache<BareAct[]>(CACHE_KEYS.BARE_ACTS);
    if (cachedData) {
      console.log('Returning all bare acts from cache');
      
      // Return cached data immediately, but refresh cache in background if it's older
      const cacheTime = localStorage.getItem(`${CACHE_KEYS.BARE_ACTS}_timestamp`);
      const now = Date.now();
      if (cacheTime && (now - parseInt(cacheTime)) > 60 * 60 * 1000) { // 1 hour
        console.log('Refreshing bare acts cache in background');
        setTimeout(() => {
          refreshBareActsCache();
        }, 100);
      }
      
      return cachedData;
    }
    
    return await fetchBareActsFromDb();
  } catch (err) {
    console.error('Failed to fetch bare acts:', err);
    return [];
  }
};

/**
 * Background refresh of cache
 */
const refreshBareActsCache = async (): Promise<void> => {
  try {
    await fetchBareActsFromDb();
    localStorage.setItem(`${CACHE_KEYS.BARE_ACTS}_timestamp`, Date.now().toString());
  } catch (err) {
    console.error('Background cache refresh failed:', err);
  }
};

/**
 * Direct fetch from database
 */
const fetchBareActsFromDb = async (): Promise<BareAct[]> => {
  console.log('Fetching all bare acts from database');
  
  const { data, error } = await supabase
    .from('bare_acts')
    .select('*')
    .order('title');

  if (error) {
    console.error('Error fetching bare acts:', error);
    return [];
  }

  // Save to cache with timestamp
  setToCache(CACHE_KEYS.BARE_ACTS, data, CACHE_KEYS.TTL.MEDIUM);
  localStorage.setItem(`${CACHE_KEYS.BARE_ACTS}_timestamp`, Date.now().toString());
  
  return data ?? [];
};

/**
 * Fetch bare acts by category
 */
export const fetchBareActsByCategory = async (category: string): Promise<BareAct[]> => {
  try {
    // Try to get from cache first
    const cacheKey = CACHE_KEYS.BARE_ACTS_BY_CATEGORY(category);
    const cachedData = getFromCache<BareAct[]>(cacheKey);
    if (cachedData) {
      console.log(`Returning bare acts for category ${category} from cache`);
      return cachedData;
    }
    
    // If we have all acts cached, filter them client-side instead of a new request
    const allCachedActs = getFromCache<BareAct[]>(CACHE_KEYS.BARE_ACTS);
    if (allCachedActs) {
      console.log(`Filtering cached bare acts for category ${category}`);
      const filteredActs = allCachedActs.filter(act => act.category === category);
      setToCache(cacheKey, filteredActs, CACHE_KEYS.TTL.MEDIUM);
      return filteredActs;
    }
    
    console.log(`Fetching bare acts for category ${category} from database`);
    const { data, error } = await supabase
      .from('bare_acts')
      .select('*')
      .eq('category', category)
      .order('title');

    if (error) {
      console.error(`Error fetching bare acts for category ${category}:`, error);
      return [];
    }

    // Save to cache
    setToCache(cacheKey, data, CACHE_KEYS.TTL.MEDIUM);
    
    return data ?? [];
  } catch (err) {
    console.error(`Failed to fetch bare acts for category ${category}:`, err);
    return [];
  }
};

/**
 * Fetch featured bare acts with optimized loading
 */
export const fetchFeaturedBareActs = async (limit: number = 5): Promise<BareAct[]> => {
  try {
    // Try to get from cache first
    const cacheKey = CACHE_KEYS.FEATURED_BARE_ACTS(limit);
    const cachedData = getFromCache<BareAct[]>(cacheKey);
    if (cachedData) {
      console.log(`Returning featured bare acts (limit: ${limit}) from cache`);
      return cachedData;
    }
    
    // If we have all acts cached, filter them client-side for featured ones
    const allCachedActs = getFromCache<BareAct[]>(CACHE_KEYS.BARE_ACTS);
    if (allCachedActs) {
      console.log(`Filtering cached bare acts for featured (limit: ${limit})`);
      const filteredActs = allCachedActs
        .filter(act => act.is_featured)
        .slice(0, limit);
      
      setToCache(cacheKey, filteredActs, CACHE_KEYS.TTL.SHORT);
      return filteredActs;
    }
    
    console.log(`Fetching featured bare acts (limit: ${limit}) from database`);
    const { data, error } = await supabase
      .from('bare_acts')
      .select('*')
      .eq('is_featured', true)
      .order('title')
      .limit(limit);

    if (error) {
      console.error('Error fetching featured bare acts:', error);
      return [];
    }

    // Save to cache
    setToCache(cacheKey, data, CACHE_KEYS.TTL.SHORT);
    
    return data ?? [];
  } catch (err) {
    console.error('Failed to fetch featured bare acts:', err);
    return [];
  }
};

/**
 * Fetch a bare act by its slug with optimized loading strategy
 */
export const fetchBareActBySlug = async (slug: string): Promise<BareActWithSections | null> => {
  try {
    // Try to get from cache first for instant loading
    const cacheKey = CACHE_KEYS.BARE_ACT_DETAIL(slug);
    const cachedData = getFromCache<BareActWithSections>(cacheKey);
    if (cachedData) {
      console.log(`Returning bare act details for '${slug}' from cache`);
      return cachedData;
    }
    
    console.log(`Fetching bare act details for '${slug}' from database`);
    
    // For slow connections, try to get basic info from all-acts cache first
    let basicAct: BareAct | null = null;
    if (isLowBandwidth()) {
      const allCachedActs = getFromCache<BareAct[]>(CACHE_KEYS.BARE_ACTS);
      if (allCachedActs) {
        basicAct = allCachedActs.find(act => act.slug === slug) || null;
      }
    }
    
    // First get the bare act
    const { data: act, error } = await supabase
      .from('bare_acts')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error || !act) {
      console.error(`Error fetching bare act with slug ${slug}:`, error);
      return basicAct ? { 
        ...basicAct, 
        sections: [],
        related_acts: []
      } : null;
    }

    // Then get the sections
    const { data: sections, error: sectionsError } = await supabase
      .from('bare_act_sections')
      .select('*')
      .eq('act_id', act.id)
      .order('order_index');

    if (sectionsError) {
      console.error(`Error fetching sections for act ${act.id}:`, sectionsError);
    }

    // Get related acts
    const { data: relatedActIds, error: relatedError } = await supabase
      .from('bare_act_related')
      .select('related_act_id')
      .eq('act_id', act.id);

    let relatedActs: BareAct[] = [];
    if (!relatedError && Array.isArray(relatedActIds) && relatedActIds.length > 0) {
      const relatedIds = relatedActIds.map(r => r.related_act_id);
      
      const { data: related, error: relatedActsError } = await supabase
        .from('bare_acts')
        .select('*')  // Select all fields to match BareAct type
        .in('id', relatedIds);

      if (!relatedActsError && related) {
        relatedActs = related;
      }
    }

    const result = {
      ...act,
      sections: sections || [],
      related_acts: relatedActs
    };
    
    // Save to cache
    setToCache(cacheKey, result, CACHE_KEYS.TTL.MEDIUM);
    
    return result;
  } catch (err) {
    console.error(`Failed to fetch bare act with slug ${slug}:`, err);
    return null;
  }
};

/**
 * Search bare acts with optimized client-side search when possible
 */
export const searchBareActs = async (query: string): Promise<BareAct[]> => {
  try {
    if (!query) return [];
    
    // Try to get from cache first
    const cacheKey = CACHE_KEYS.SEARCH_RESULTS(query);
    const cachedData = getFromCache<BareAct[]>(cacheKey);
    if (cachedData) {
      console.log(`Returning search results for '${query}' from cache`);
      return cachedData;
    }
    
    // Try client-side search if all acts are cached
    const allCachedActs = getFromCache<BareAct[]>(CACHE_KEYS.BARE_ACTS);
    const searchTermLower = query.toLowerCase();
    
    if (allCachedActs) {
      console.log(`Performing client-side search for '${query}'`);
      const results = allCachedActs.filter(act => 
        act.title.toLowerCase().includes(searchTermLower) || 
        act.description.toLowerCase().includes(searchTermLower)
      );
      
      // Cache the results
      setToCache(cacheKey, results, CACHE_KEYS.TTL.SHORT);
      return results;
    }
    
    console.log(`Searching bare acts for '${query}' in database`);
    const { data, error } = await supabase
      .from('bare_acts')
      .select('*')
      .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
      .order('title');

    if (error) {
      console.error(`Error searching bare acts for "${query}":`, error);
      return [];
    }
    
    // Save to cache (shorter TTL for search results as they might change more often)
    setToCache(cacheKey, data, CACHE_KEYS.TTL.SHORT);
    
    return data ?? [];
  } catch (err) {
    console.error(`Failed to search bare acts for "${query}":`, err);
    return [];
  }
};

// Preload frequently accessed data in the background
export const preloadCommonData = (): void => {
  setTimeout(() => {
    // Preload all bare acts in background
    fetchAllBareActs().catch(err => 
      console.error('Failed to preload bare acts:', err)
    );
    
    // Preload featured bare acts in background
    fetchFeaturedBareActs().catch(err => 
      console.error('Failed to preload featured acts:', err)
    );
  }, 2000); // Wait 2 seconds after page load before preloading
};